import { useState } from 'react'
import type { PrinterResponse } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import {
  PageHeader,
  DataTable,
  Pagination,
  StatusBadge,
  ColumnSettingsButton,
  btnPrimary,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { PrinterEditModal } from './PrinterEditModal'
import { useTableColumns } from '../../hooks/use-table-columns'

const DRIVER_LABELS: Record<string, string> = {
  dymo_client: 'DYMO',
  zebra_browser_print: 'Zebra',
}

// StatusBadge doesn't know 'dymo' or 'zebra' — use inline styles
function DriverBadge({ driver }: { driver: string }) {
  const isZebra = driver === 'zebra_browser_print'
  return (
    <span
      style={{
        padding: '0.2rem 0.6rem',
        borderRadius: '9999px',
        fontSize: '0.8125rem',
        fontWeight: 500,
        background: isZebra ? '#ede9fe' : '#dbeafe',
        color: isZebra ? '#5b21b6' : '#1e40af',
        whiteSpace: 'nowrap',
      }}
    >
      {DRIVER_LABELS[driver] ?? driver}
    </span>
  )
}

export function PrinterListPage() {
  const { data, total, page, loading, setPage, refresh } = useApiList<PrinterResponse>({
    path: '/admin/printers',
  })
  const { tableId: _, ...tableColumnProps } = useTableColumns('printers')

  const [modalOpen, setModalOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isNew, setIsNew] = useState(false)

  function openEditModal(printer: PrinterResponse) {
    setSelectedId(printer.id)
    setIsNew(false)
    setModalOpen(true)
  }

  function openNewModal() {
    setSelectedId(null)
    setIsNew(true)
    setModalOpen(true)
  }

  const columns: readonly Column<PrinterResponse>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      filterable: true,
      render: (p) => <strong>{p.name}</strong>,
    },
    {
      key: 'driver',
      header: 'Driver',
      sortable: true,
      render: (p) => <DriverBadge driver={p.driver} />,
    },
    {
      key: 'model',
      header: 'Model',
      sortable: true,
      render: (p) => p.model,
    },
    {
      key: 'isDefault',
      header: 'Default',
      render: (p) => (p.isDefault ? <StatusBadge status="active" label="Default" /> : null),
    },
    {
      key: 'isActive',
      header: 'Status',
      render: (p) =>
        p.isArchived ? (
          <StatusBadge status="inactive" label="Archived" />
        ) : (
          <StatusBadge status="active" label="Active" />
        ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="Printers"
        subtitle={`${total} total`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button type="button" onClick={openNewModal} style={btnPrimary}>
              New Printer
            </button>
          </>
        }
      />
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={openEditModal}
            emptyMessage="No printers configured."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      <PrinterEditModal
        printerId={selectedId}
        isOpen={modalOpen}
        isNew={isNew}
        onClose={() => setModalOpen(false)}
        onSaved={() => {
          setModalOpen(false)
          refresh()
        }}
        onArchived={() => {
          setModalOpen(false)
          refresh()
        }}
      />
    </div>
  )
}
